import { Fragment, useContext, useState } from 'react';

import { getLinkAriaLabel, getUrlText } from '@/utils/urlUtils';

import { AspectRatioImage } from '@/components/AspectRatioImage/AspectRatioImage';
import { useCompoundContext } from '@/components/Header/context';
import LinkWithLocale from '@/components/LinkWithLocale/LinkWithLocale';
import { navToFindStores } from '@/components/Megamenu/helpers';
import { Typography } from '@/components/Typography';
import type {
  ContentfulMainHeaderFragment,
  ContentfulMainMenuItemFragment,
} from '@/graphql/contentful/api';
import { useRouter } from '@/hooks/useRouter';
import { LocaleContext } from '@/providers/LocaleProvider';
import { useRegionProvider } from '@/providers/RegionProvider';

import { MobileMenuAccordion } from '../MobileMenuAccordion';

import * as S from './MobileNav.styles';

export interface MobileNavProps {
  items: ContentfulMainHeaderFragment;
  onClose: (isOpen: boolean) => void;
  top: number;
}

export const MobileNav = ({ items, onClose }: MobileNavProps) => {
  useCompoundContext();
  const [isOpenSubDrawer, setIsOpenSubDrawer] = useState<boolean>(false);
  const [isOpenSubmenu, setIsOpenSubmenu] = useState<{
    [key: string]: boolean;
  }>({});

  const toggleSubmenu = (key: string) => {
    setIsOpenSubmenu((prev) => {
      const newState: { [key: string]: boolean } = {};

      // Close all submenus
      Object.keys(prev).forEach((key) => {
        newState[key] = false;
      });

      // Then open the selected submenu
      newState[key] = true;

      return newState;
    });
  };

  const openSubmenu = (item: ContentfulMainMenuItemFragment | null) => {
    if (!item) {
      return;
    }
    toggleSubmenu(item.internalName as string);
  };

  function makeSubmenu(item: ContentfulMainMenuItemFragment | null) {
    if (!item) {
      return null;
    }

    const submenuContent = item?.submenuItemsCollection?.items.map(
      (subItem, index) => {
        const isItemWithChildren = subItem?.submenuItemsCollection?.items || [];

        if (item?.type === 'Collection') {
          return (
            <CollectionItem
              key={subItem?.internalName}
              idx={index}
              item={subItem as ContentfulMainMenuItemFragment}
              onClose={onClose}
            />
          );
        }

        if (
          subItem?.visibleInMobile &&
          (isItemWithChildren?.length as number) > 0
        ) {
          return (
            <ItemWithChildren
              key={subItem?.internalName}
              data-test-id={`MobileNavItemWithChildren-${subItem?.internalName}`}
              item={subItem as ContentfulMainMenuItemFragment}
              onClose={onClose}
            />
          );
        }

        return null;
      },
    );

    if (item?.type === 'Collection') {
      return (
        <S.CollectionBox>
          <S.MobileCollectionBox data-test-id='MobileNavCollectionBox'>
            {submenuContent}
          </S.MobileCollectionBox>
          {!!item?.bottomLinkLabel && (
            <S.LinkBox data-test-id='MobileCollectionsLinkBox'>
              <LinkWithLocale
                aria-label={item?.bottomLinkAriaLabel ?? ''}
                href={item?.bottomLinkUrl ?? ''}
                onClick={() => onClose(false)}
              >
                {item?.bottomLinkLabel}
              </LinkWithLocale>
            </S.LinkBox>
          )}
        </S.CollectionBox>
      );
    }

    return submenuContent;
  }

  return (
    <S.Wrapper data-test-id='MobileNavWrapper'>
      <S.SubmenuWrapper isOpen={isOpenSubDrawer}>
        {items?.megamenu?.menuItemsCollection?.items.length && (
          <S.List>
            {items?.megamenu?.menuItemsCollection?.items.map((item, index) => {
              return (
                <Fragment key={index}>
                  <S.SubmenuHeaderWrapper
                    key={item?.internalName}
                    data-test-id={`MobileNavSubmenuHeaderWrapper-${index}`}
                    data-testid={`MobileNavSubmenuHeaderWrapper-${index}`}
                    onClick={() => {
                      setIsOpenSubDrawer(false);
                    }}
                    style={{
                      display: isOpenSubmenu[item?.internalName ?? '']
                        ? 'block'
                        : 'none',
                    }}
                  >
                    <S.SubmenuHeader data-test-id='MobileNavSubmenuHeader'>
                      <Typography scale='h4'>{item?.label}</Typography>
                      <S.ArrowRightIcon />
                    </S.SubmenuHeader>
                  </S.SubmenuHeaderWrapper>
                  <li
                    data-test-id='MobileNavSubmenuItems'
                    style={{
                      display: isOpenSubmenu[item?.internalName ?? '']
                        ? 'block'
                        : 'none',
                    }}
                  >
                    {makeSubmenu(item)}
                  </li>
                </Fragment>
              );
            })}
          </S.List>
        )}
      </S.SubmenuWrapper>

      {items?.megamenu?.menuItemsCollection?.items.length && (
        <S.List>
          {items?.megamenu?.menuItemsCollection?.items.map((item, index) => {
            const isItemWithChildren = item?.submenuItemsCollection?.items;

            return (
              item?.visibleInMobile &&
              ((isItemWithChildren?.length as number) > 0 ? (
                <S.ParentItem key={item?.internalName}>
                  <S.ItemContent
                    data-test-id={`MobileNavParentItem-${index}`}
                    data-testid={`MobileNavParentItem-${index}`}
                    onClick={() => {
                      setIsOpenSubDrawer(true);
                      openSubmenu(item as ContentfulMainMenuItemFragment);
                    }}
                  >
                    <Typography scale='h4'>
                      <LinkWithLocale
                        aria-label={getLinkAriaLabel(item.url ?? '')}
                        href={item.url ?? ''}
                      >
                        {item?.label}
                      </LinkWithLocale>
                    </Typography>
                    <S.ArrowRightIcon />
                  </S.ItemContent>
                </S.ParentItem>
              ) : (
                <LinkItem
                  key={item?.internalName}
                  item={item as ContentfulMainMenuItemFragment}
                  onClose={onClose}
                />
              ))
            );
          })}
        </S.List>
      )}
    </S.Wrapper>
  );
};

export const CollectionItem = ({
  idx,
  item,
  onClose,
}: {
  idx: number;
  item: ContentfulMainMenuItemFragment;
  onClose: (isOpen: boolean) => void;
}) => {
  const router = useRouter();
  const onCloseMobileMenu = () => {
    if (item?.openUrlInNewTab) {
      window.open(item?.url ?? '/', '_blank');
    } else {
      router.push(item?.url ?? '/');
    }
    onClose(false);
  };

  return (
    <S.Collection
      key={item?.internalName}
      data-test-id={`MobileNavCollectionItem-${item?.internalName}`}
      onClick={() => onCloseMobileMenu()}
    >
      <S.CollectionItem>
        <AspectRatioImage
          alt={item.image?.title ?? ''}
          aspectRatio={{
            x: item.image?.width ?? 1,
            y: item.image?.height ?? 1,
          }}
          className='image'
          data-test-id={`CollectionsCollectionItemImage-${idx}`}
          focalPoint={{
            x: (item.image?.width ?? 0) / 2,
            y: (item.image?.height ?? 0) / 2,
          }}
          loaderProps={{ height: 175 }}
          loading='lazy'
          originalImageDimensions={{
            x: item.image?.width ?? 0,
            y: item.image?.height ?? 0,
          }}
          showFallbackPlaceholder
          sizes='(max-width: 600px) 50vw, 20vw'
          src={item.image?.url ?? ''}
          useLoader
          width='100%'
        />
        <S.CollectionTitle data-test-id={`CollectionsCollectionTitle-${idx}`}>
          {item.label}
        </S.CollectionTitle>
      </S.CollectionItem>
    </S.Collection>
  );
};

const LinkItem = ({
  item,
  onClose,
}: {
  item: ContentfulMainMenuItemFragment;
  onClose: (isOpen: boolean) => void;
}) => {
  const locale = useContext(LocaleContext);
  const {
    country: { code },
  } = useRegionProvider();
  const url =
    getUrlText(item?.url ?? '') === '/stores'
      ? navToFindStores(locale, code)
      : getUrlText(item?.url ?? '');

  return (
    <S.ListItem data-test-id={`MobileNavLinkItem-${item?.internalName}`}>
      <S.LinkItem
        aria-label={getLinkAriaLabel(getUrlText(item?.url ?? ''))}
        as={getUrlText(item?.url ?? '') === '/stores' ? 'a' : undefined}
        href={url}
        onClick={() => onClose(false)}
        target={item?.openUrlInNewTab ? '_blank' : '_self'}
      >
        <Typography scale='h4'>{item.label}</Typography>
      </S.LinkItem>
    </S.ListItem>
  );
};

export const ItemWithChildren = ({
  item,
  onClose,
}: {
  item: ContentfulMainMenuItemFragment;
  onClose: (isOpen: boolean) => void;
}) => {
  const locale = useContext(LocaleContext);
  const {
    country: { code },
  } = useRegionProvider();

  return (
    <S.ListItemContent
      data-test-id={`MobileNavInnerListItem-${item?.internalName}`}
    >
      <MobileMenuAccordion title={item.label ?? ''}>
        <S.InnerList>
          {item?.submenuItemsCollection?.items?.map(
            (link) =>
              link?.visibleInMobile && (
                <S.InnerListItem
                  key={link?.internalName}
                  data-test-id={`MobileNavInnerListItem-${link?.internalName}`}
                >
                  <S.LinkItem
                    aria-label={getLinkAriaLabel(
                      getUrlText(link?.url ?? ''),
                      link.label ?? '',
                    )}
                    as={
                      getUrlText(link?.url ?? '') === '/stores'
                        ? 'a'
                        : undefined
                    }
                    href={
                      getUrlText(link?.url ?? '') === '/stores'
                        ? navToFindStores(locale, code)
                        : getUrlText(link?.url ?? '')
                    }
                    onClick={() => onClose(false)}
                    target={link.openUrlInNewTab ? '_blank' : '_self'}
                  >
                    {link?.label}
                  </S.LinkItem>
                </S.InnerListItem>
              ),
          )}
        </S.InnerList>
      </MobileMenuAccordion>
    </S.ListItemContent>
  );
};

MobileNav.displayName = 'MobileNav';
